import { useState, useEffect } from "react"
import { useAuth } from "@/context/auth/auth-context"
import { Analytics } from "@/lib/analytics"
import hive from "@/lib/api/hive"
import type { NotificationPreferences } from "@/lib/api/hive"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Bell, Mail, CalendarDays, Loader2 } from "lucide-react"

export function NotificationPreferencesPage() {
	const { token, user } = useAuth()
	const [prefs, setPrefs] = useState<NotificationPreferences | null>(null)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState<"weekly" | "monthly" | null>(null)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (!token) return
		hive.getNotificationPreferences(token)
			.then(setPrefs)
			.catch((err) => setError(err instanceof Error ? err.message : "Failed to load preferences"))
			.finally(() => setLoading(false))
	}, [token])

	const handleToggle = async (field: "weekly_digest" | "monthly_digest") => {
		if (!token || !prefs || saving) return
		const key = field === "weekly_digest" ? "weekly" : "monthly"
		setSaving(key)
		const updated = { [field]: !prefs[field] }
		try {
			const result = await hive.updateNotificationPreferences(updated, token)
			setPrefs(result)
			Analytics.capture("Digest Toggled", { type: key, enabled: !prefs[field] })
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to update preferences")
		} finally {
			setSaving(null)
		}
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		)
	}

	return (
		<div className="max-w-xl mx-auto px-4 py-10">
			<div className="flex items-center gap-3 mb-8">
				<div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
					<Bell className="h-5 w-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div>
					<h1 className="text-2xl font-bold text-foreground">Email Preferences</h1>
					<p className="text-sm text-muted-foreground">Manage your digest emails</p>
				</div>
			</div>

			{error && (
				<div className="mb-6 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-600 dark:text-red-400">
					{error}
				</div>
			)}

			<Card className="border border-border/60">
				<CardHeader className="pb-4">
					<CardTitle className="text-base font-semibold flex items-center gap-2">
						<Mail className="h-4 w-4 text-muted-foreground" />
						Digest Emails
					</CardTitle>
					<CardDescription>
						Sent to{" "}
						<span className="font-medium text-foreground">{user?.email ?? "your registered email"}</span>.
						You can unsubscribe from individual digests at any time.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* Weekly digest */}
					<div className="flex items-start justify-between gap-4">
						<div className="flex items-start gap-3">
							<div className="mt-0.5 h-8 w-8 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
								<CalendarDays className="h-4 w-4 text-amber-600 dark:text-amber-400" />
							</div>
							<div>
								<Label htmlFor="weekly-toggle" className="text-sm font-medium cursor-pointer">
									Weekly Digest
								</Label>
								<p className="text-xs text-muted-foreground mt-0.5">
									Every Saturday — dividends received, portfolio snapshot, week-ahead events, and FIRE
									progress.
								</p>
							</div>
						</div>
						<div className="flex items-center gap-2 flex-shrink-0 mt-1">
							{saving === "weekly" && (
								<Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
							)}
							<Switch
								id="weekly-toggle"
								checked={prefs?.weekly_digest ?? true}
								onCheckedChange={() => handleToggle("weekly_digest")}
								disabled={saving !== null}
							/>
						</div>
					</div>

					<div className="border-t border-border/60" />

					{/* Monthly digest */}
					<div className="flex items-start justify-between gap-4">
						<div className="flex items-start gap-3">
							<div className="mt-0.5 h-8 w-8 rounded-md bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center flex-shrink-0">
								<CalendarDays className="h-4 w-4 text-sky-600 dark:text-sky-400" />
							</div>
							<div>
								<Label htmlFor="monthly-toggle" className="text-sm font-medium cursor-pointer">
									Monthly Digest
								</Label>
								<p className="text-xs text-muted-foreground mt-0.5">
									1st of each month — full month income recap, month-over-month and year-over-year
									comparisons, dividend changes.
								</p>
							</div>
						</div>
						<div className="flex items-center gap-2 flex-shrink-0 mt-1">
							{saving === "monthly" && (
								<Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
							)}
							<Switch
								id="monthly-toggle"
								checked={prefs?.monthly_digest ?? true}
								onCheckedChange={() => handleToggle("monthly_digest")}
								disabled={saving !== null}
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			<p className="text-xs text-muted-foreground mt-6 text-center">
				You can also unsubscribe directly from the footer link in any digest email.
			</p>
		</div>
	)
}
